import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useSuspense } from '@rest-hooks/react';
import { useHistory, useParams } from 'react-router-dom';

import OrderResource from 'resources/user/OrderResource';
import UserAccountResource from 'resources/user/UserAccountResource';

import StyledTextField from 'ui/StyledComponents/StyledTextField';

import FormSection from 'ui/FormSection';
import Button from 'ui/Button';
import { useError } from 'utils/useErrorController';
import { validateEmail } from 'utils/validations/validateEmail';
import { checkLoginStatus } from 'utils/checkLoginStatus';

import { t } from "i18n/index";

// Lite-account / partial-auth buyers reach this screen too. UserAccountResource
// extends AuthenticatedResource and would 401 for them — a 401 triggers
// localStorage.clear + redirect to /login, destroying the checkout flow. Skip
// the prefill fetch for those buyers; they'll just start with an empty first
// ticket email field instead.
const TicketDetails = (props) => {
    if (checkLoginStatus()) {
        return <AuthenticatedTicketDetails {...props} />;
    }
    return <TicketDetailsBase {...props} email='' />;
};

const AuthenticatedTicketDetails = (props) => {
    const { email } = useSuspense(UserAccountResource.detail(), { id: 1 });
    return <TicketDetailsBase {...props} email={email} />;
};

const TicketDetailsBase = ({setStep, handleClaimReset, paymentIntentId, amount, cartItems, email}) => {

    const {fetch} = useController();
    const {organizationSlug} = useParams();
    const history = useHistory();
    const isCartMode = cartItems && cartItems.length > 0;
    const getOrderItemIndex = (ci, fallbackIndex) => ci.orderItemIndex ?? fallbackIndex;
    const findCartItemByOrderIndex = (orderItemIndex) =>
      cartItems.find((ci, index) => String(getOrderItemIndex(ci, index)) === String(orderItemIndex));

    // For cart mode: emails keyed by item index → array of emails per item
    // For single mode: flat array of emails
    const buildInitialEmails = () => {
      if (isCartMode) {
        const emailsByItem = {};
        cartItems.forEach((ci, index) => {
          emailsByItem[getOrderItemIndex(ci, index)] = Array.from({ length: ci.amount }, (_, i) => (i === 0 ? email : undefined));
        });
        return emailsByItem;
      }
      return Array.from({ length: amount }, (_, index) => (index === 0 ? email : undefined));
    };

    const [emails, setEmails] = useState(buildInitialEmails);
    const {handleError, setLoading, loading} = useError();

    const handleRedirect = () => {
      handleClaimReset();
      history.push(`/${organizationSlug}`);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            if (isCartMode) {
              // Validate all emails across all items
              Object.entries(emails).forEach(([itemIndex, itemEmails]) => {
                const cartItem = findCartItemByOrderIndex(itemIndex);
                itemEmails.forEach((em, emailIndex) => {
                  validateEmail(em, '', `${t('errors:invalidTicketEmail')} ${cartItem?.product?.title || ''} #${emailIndex + 1}`);
                });
              });
              await fetch(OrderResource.update(), {id: paymentIntentId}, {organization_slug: organizationSlug, emails});
            } else {
              emails.forEach((em, index) => {
                validateEmail(em, '', `${t('errors:invalidTicketEmail')} ${index+1}`);
              });
              await fetch(OrderResource.update(), {id: paymentIntentId}, {organization_slug: organizationSlug, emails});
            }

            setStep(4);
            setLoading(false);
        } catch (error) {
            handleError(error);
        }
    };

    const handleEmailChange = (indexOrItemIndex, eventOrEmailIndex, maybeEvent) => {
        if (isCartMode) {
          const itemIndex = indexOrItemIndex;
          const emailIndex = eventOrEmailIndex;
          const event = maybeEvent;
          setEmails(prev => {
            const updated = { ...prev };
            const itemEmails = [...(updated[itemIndex] || [])];
            itemEmails[emailIndex] = event.target.value;
            updated[itemIndex] = itemEmails;
            return updated;
          });
        } else {
          const index = indexOrItemIndex;
          const event = eventOrEmailIndex;
          const newEmails = [...emails];
          newEmails[index] = event.target.value;
          setEmails(newEmails);
        }
    };

    const renderTextField = (label, name, value, onChange) => {
        return (
            <StyledTextField
              key={name}
              label={label}
              name={name}
              value={value || ''}
              onChange={onChange}
              fullWidth
              size='small'
              color="warning">
            </StyledTextField>
        );
    };

    const renderCartFields = () => {
      return cartItems.map((ci, itemIndex) => {
        const orderItemIndex = getOrderItemIndex(ci, itemIndex);

        return (
          <div key={orderItemIndex}>
            <FormSection title={ci.product.title} slim>
              {Array.from({ length: ci.amount }, (_, emailIndex) =>
                renderTextField(
                  `Ticket ${emailIndex + 1} email`,
                  `email-${orderItemIndex}-${emailIndex}`,
                  (emails[orderItemIndex] || [])[emailIndex] || '',
                  (event) => handleEmailChange(orderItemIndex, emailIndex, event)
                )
              )}
            </FormSection>
          </div>
        );
      });
    };

    const renderSingleFields = () => {
      return Array.from({length: amount}, (_, i) =>
        renderTextField(`Ticket ${i+1} email`, `email ${i+1}`, emails[i] || '', (event) => handleEmailChange(i, event)));
    };

    return (
    <div className='payment-processed'>

        {isCartMode ? (
          renderCartFields()
        ) : (
          <FormSection title={t('literal:ticketDetails')} slim>
            {renderSingleFields()}
          </FormSection>
        )}

        <div className='button-group'>

            <Button onClick={handleRedirect} fullwidth secondary loading={loading}>{t('literal:close')}</Button>

            <Button onClick={handleSubmit} fullwidth loading={loading}>{t('literal:submit')}</Button>

        </div>
    </div>
  );
};

const ticketDetailsPropTypes = {
  setStep: PropTypes.func,
  handleClaimReset: PropTypes.func,
  paymentIntentId: PropTypes.string,
  amount: PropTypes.number,
  cartItems: PropTypes.array,
};

TicketDetails.propTypes = ticketDetailsPropTypes;
AuthenticatedTicketDetails.propTypes = ticketDetailsPropTypes;
TicketDetailsBase.propTypes = {
  ...ticketDetailsPropTypes,
  email: PropTypes.string,
};

export default TicketDetails;
